import React, { useEffect, useState } from "react";
import { Loader2, Plus, Info, Shield, MessageCircle, Megaphone, Trash2, Eye, X, ClipboardPaste } from "lucide-react";
import { api, formatError } from "../lib/api";
import AppShell from "../components/AppShell";

/**
 * Account Knowledge — owner (or super_admin) view for managing account-scoped
 * documents fed into the Warren assistant. Consent flags are the source of
 * truth for what the assistant may retrieve.
 */
export default function AccountKnowledgePage() {
  const [account, setAccount] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [pendingMarketing, setPendingMarketing] = useState(null); // {doc, next}
  const [reading, setReading] = useState(null); // full doc opened in read modal

  const reload = async () => {
    setLoading(true);
    try {
      const [a, list] = await Promise.all([
        api.get("/accounts/me"),
        api.get("/account/kb/documents"),
      ]);
      setAccount(a.data);
      setDocs(list.data?.documents || []);
      setError(null);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const isOwner = account?.membership?.role_in_account === "owner";

  const createDoc = async (evt) => {
    evt.preventDefault();
    if (newContent.trim().length < 1) return;
    setCreating(true);
    try {
      // Title is optional in the UI now — the paste box is the hero.
      // If the founder didn't give one, use the first line of the
      // pasted content (trimmed to 80 chars) so the doc still has a
      // human-readable label in the list.
      const derivedTitle = (newTitle.trim()
        || newContent.trim().split(/\r?\n/)[0].slice(0, 80)
        || "Untitled note");
      await api.post("/account/kb/documents", {
        title: derivedTitle,
        content: newContent.trim(),
      });
      setNewTitle(""); setNewContent("");
      await reload();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setCreating(false);
    }
  };

  const applyConsent = async (doc, patch) => {
    try {
      await api.patch(`/account/kb/documents/${doc.id}`, patch);
      await reload();
    } catch (err) {
      setError(formatError(err));
    }
  };

  const softDelete = async (doc) => {
    if (!window.confirm(`Remove "${doc.title}" from Warren's knowledge?`)) return;
    await applyConsent(doc, { is_active: false });
  };

  return (
    <AppShell title="Account Knowledge">
      <div className="w-full py-4 md:py-6 space-y-6" data-testid="account-knowledge-page">
        <header className="space-y-1">
          <h1 className="text-2xl md:text-3xl font-bold text-neutral-900">Account Knowledge</h1>
          <p className="text-sm text-neutral-600">
            Paste facts, positioning, guidelines, or any reference notes below and Morgan will use them
            <span className="font-semibold"> only</span> for your account. Every consent starts off.
          </p>
          {account?.account?.name && (
            <div className="pt-1 text-xs text-neutral-500">
              Account: <span className="font-mono text-neutral-700">{account.account.name}</span>
              {" · "}Membership: <span className="font-mono">{account.membership?.role_in_account}</span>
            </div>
          )}
        </header>

        {!isOwner && (
          <div className="rounded-md border border-pink-200 bg-pink-50 text-pink-900 text-sm px-3 py-2 flex items-start gap-2">
            <Info className="h-4 w-4 mt-[3px]" />
            <div>You can query these documents through Morgan, but only the primary account owner can add or change consents.</div>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">{error}</div>
        )}

        {/* Always-visible paste card — hero of the page for owners. */}
        {isOwner && (
          <form
            onSubmit={createDoc}
            className="rounded-xl border-2 border-dashed border-pink-300 bg-gradient-to-br from-pink-50 to-white p-5 space-y-3"
            data-testid="knowledge-new-form"
          >
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 grid place-items-center rounded-lg bg-gradient-to-br from-pink-500 to-fuchsia-600 text-white shadow-sm">
                <ClipboardPaste className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-neutral-900 leading-tight">Paste new info for Morgan</div>
                <div className="text-[11px] text-neutral-500 leading-tight">Cut &amp; paste any text — Morgan will remember it once you turn a consent on.</div>
              </div>
            </div>
            <input
              data-testid="knowledge-new-title"
              value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
              maxLength={200}
              placeholder="Title (optional — we'll use the first line if empty)"
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/40 focus:border-pink-400"
            />
            <textarea
              data-testid="knowledge-new-content"
              value={newContent} onChange={(e) => setNewContent(e.target.value)}
              rows={6} maxLength={20000}
              placeholder="Paste any facts, positioning, brand voice notes, product specs, buyer meeting recaps, pricing rationale, competitor notes…"
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/40 focus:border-pink-400 resize-y"
            />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] text-neutral-500">
                {newContent.length > 0
                  ? <><span className="font-mono">{newContent.length.toLocaleString()}</span> / 20,000 characters</>
                  : <>Tip: press <kbd className="px-1 py-0.5 rounded bg-neutral-100 border border-neutral-300 font-mono">⌘V</kbd> / <kbd className="px-1 py-0.5 rounded bg-neutral-100 border border-neutral-300 font-mono">Ctrl+V</kbd> to paste.</>}
              </div>
              <div className="flex items-center gap-2">
                {(newTitle || newContent) && (
                  <button
                    type="button"
                    onClick={() => { setNewTitle(""); setNewContent(""); }}
                    className="text-xs text-neutral-500 hover:text-neutral-900"
                    data-testid="knowledge-new-clear"
                  >Clear</button>
                )}
                <button
                  type="submit"
                  disabled={creating || newContent.trim().length < 1}
                  data-testid="knowledge-create-submit"
                  className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-br from-pink-500 to-fuchsia-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-40 hover:shadow-md transition"
                >
                  {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  {creating ? "Saving…" : "Save to knowledge"}
                </button>
              </div>
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-sm text-neutral-500 flex items-center gap-2" data-testid="knowledge-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : docs.length === 0 ? (
          <div className="text-sm text-neutral-600 rounded-lg border border-dashed border-neutral-300 bg-white px-6 py-10 text-center" data-testid="knowledge-empty">
            No account documents yet. {isOwner ? "Paste something above to teach Morgan about your brand." : "Ask the account owner to add one."}
          </div>
        ) : (
          <ul className="grid gap-3">
            {docs.map((d) => (
              <li key={d.id} className="rounded-lg border border-neutral-200 bg-white p-4" data-testid={`knowledge-doc-${d.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="text-base font-semibold text-neutral-900 truncate">{d.title}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">Updated {new Date(d.updated_at).toLocaleString()}</div>
                    <p className="text-sm text-neutral-700 mt-2 line-clamp-3 whitespace-pre-wrap">{d.content}</p>
                    <button
                      type="button"
                      onClick={() => setReading(d)}
                      data-testid={`knowledge-read-${d.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-800 text-[11px] font-semibold px-2.5 py-1 transition"
                    >
                      <Eye className="h-3 w-3" /> Read full document
                    </button>
                  </div>
                  {isOwner && (
                    <button
                      onClick={() => softDelete(d)}
                      data-testid={`knowledge-delete-${d.id}`}
                      title="Delete this document"
                      aria-label="Delete document"
                      className="shrink-0 inline-flex items-center gap-1 rounded-full border border-neutral-200 hover:border-red-300 hover:bg-red-50 text-neutral-500 hover:text-red-600 text-[11px] font-semibold px-2.5 py-1 transition"
                    ><Trash2 className="h-3 w-3" /> Delete</button>
                  )}
                </div>

                <div className="mt-4 grid gap-2 md:grid-cols-3">
                  <ConsentToggle
                    label="Warren chat"
                    icon={<MessageCircle className="h-4 w-4" />}
                    hint="Warren may cite this document when you ask questions."
                    checked={!!d.consent?.user_facing_chat}
                    disabled={!isOwner}
                    testId={`consent-chat-${d.id}`}
                    onChange={(v) => applyConsent(d, { user_facing_chat: v })}
                  />
                  <ConsentToggle
                    label="Admin cross-account"
                    icon={<Shield className="h-4 w-4" />}
                    hint="Super admins may use this doc in explicit cross-account queries."
                    checked={!!d.consent?.admin_cross_account}
                    disabled={!isOwner}
                    testId={`consent-admin-${d.id}`}
                    onChange={(v) => applyConsent(d, { admin_cross_account: v })}
                  />
                  <ConsentToggle
                    label="Marketing projection"
                    icon={<Megaphone className="h-4 w-4" />}
                    hint="Consented, read-only brand facts admins may filter accounts by. Never used for outbound campaigns."
                    checked={!!d.consent?.marketing_projection}
                    disabled={!isOwner}
                    testId={`consent-mkt-${d.id}`}
                    onChange={(v) => {
                      if (v) setPendingMarketing({ doc: d, next: v });
                      else applyConsent(d, { marketing_projection: v });
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Marketing consent confirmation */}
        {pendingMarketing && (
          <div role="dialog" aria-label="Confirm marketing consent" className="fixed inset-0 z-50 grid place-items-center bg-black/40" data-testid="marketing-confirm-dialog">
            <div className="max-w-md bg-white rounded-lg shadow-xl border border-neutral-200 p-5 space-y-3">
              <div className="flex items-center gap-2 text-neutral-900 font-semibold"><Megaphone className="h-5 w-5" /> Enable marketing projection?</div>
              <p className="text-sm text-neutral-700">
                Turning this on lets admins filter accounts by structured facts drawn only from this consented document. Nothing is emailed, texted, or exported.
              </p>
              <div className="flex justify-end gap-2">
                <button className="text-sm px-3 py-1.5 rounded-md border border-neutral-300" onClick={() => setPendingMarketing(null)}>Cancel</button>
                <button
                  className="text-sm px-3 py-1.5 rounded-md bg-neutral-900 text-white"
                  data-testid="marketing-confirm-btn"
                  onClick={async () => {
                    const { doc, next } = pendingMarketing;
                    setPendingMarketing(null);
                    await applyConsent(doc, { marketing_projection: next });
                  }}
                >Enable</button>
              </div>
            </div>
          </div>
        )}
        {/* Read-only full-document modal */}
        {reading && (
          <div
            role="dialog"
            aria-label={`Read ${reading.title}`}
            data-testid="knowledge-read-dialog"
            className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
            onClick={() => setReading(null)}
          >
            <div
              className="w-full max-w-3xl max-h-[85vh] bg-white rounded-xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-neutral-200 bg-neutral-50">
                <div className="min-w-0">
                  <div className="text-lg font-bold text-neutral-900 break-words">{reading.title}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    Updated {new Date(reading.updated_at).toLocaleString()}
                    {" · "}<span className="font-mono">{(reading.content || "").length.toLocaleString()}</span> chars
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReading(null)}
                  data-testid="knowledge-read-close"
                  aria-label="Close"
                  className="shrink-0 p-1.5 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100"
                ><X className="h-4 w-4" /></button>
              </div>
              <div className="overflow-y-auto px-5 py-4 whitespace-pre-wrap text-sm text-neutral-800 leading-relaxed font-mono">
                {reading.content}
              </div>
              <div className="border-t border-neutral-200 px-5 py-3 bg-white flex items-center justify-end gap-2">
                {isOwner && (
                  <button
                    type="button"
                    onClick={async () => {
                      const doc = reading;
                      setReading(null);
                      await softDelete(doc);
                    }}
                    data-testid="knowledge-read-delete"
                    className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 hover:border-red-300 hover:bg-red-50 text-neutral-600 hover:text-red-700 text-xs font-semibold px-3 py-1.5 transition"
                  ><Trash2 className="h-3 w-3" /> Delete</button>
                )}
                <button
                  type="button"
                  onClick={() => setReading(null)}
                  className="rounded-full bg-neutral-900 text-white text-xs font-semibold px-4 py-1.5 hover:bg-neutral-800"
                >Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}


function ConsentToggle({ label, icon, hint, checked, onChange, disabled, testId }) {
  return (
    <div className={`rounded-md border ${checked ? "border-emerald-300 bg-emerald-50" : "border-neutral-200 bg-neutral-50"} p-3`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
          {icon}
          {label}
        </div>
        <label className={`inline-flex items-center cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
          <input
            data-testid={testId}
            type="checkbox" checked={checked} disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
            className="sr-only peer"
          />
          <span className="w-9 h-5 bg-neutral-300 peer-checked:bg-emerald-600 rounded-full relative transition-colors">
            <span className={`absolute top-0.5 ${checked ? "left-4" : "left-0.5"} w-4 h-4 bg-white rounded-full transition-all`} />
          </span>
        </label>
      </div>
      <div className="mt-1 text-[11px] text-neutral-600">{hint}</div>
    </div>
  );
}
